import { ReactNode, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from '@state/configureStore';
import { addToast, removeToast, selectToasts, ToastMessage } from '@state/toastSlice';

const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const toasts = useSelector(selectToasts);
    const dispatch = useDispatch();

    return (
        <Provider store={store}>
            {children}
            <ToastContainer position="bottom-start" className="p-3">
                {toasts.map((toast) => (
                    <ToastComponent key={toast.id} toast={toast} onClose={() => dispatch(removeToast(toast.id))} />
                ))}
            </ToastContainer>
        </Provider>
    );
};

const useToast = () => {
    const dispatch = useDispatch();

    const success = useCallback(
        (title: string, message: string) => {
            dispatch(addToast({ title, message, type: 'success' }));
        },
        [dispatch],
    );

    const error = useCallback(
        (title: string, message: string) => {
            dispatch(addToast({ title, message, type: 'error' }));
        },
        [dispatch],
    );

    const info = useCallback(
        (title: string, message: string) => {
            dispatch(addToast({ title, message, type: 'info' }));
        },
        [dispatch],
    );

    const warning = useCallback(
        (title: string, message: string) => {
            dispatch(addToast({ title, message, type: 'warning' }));
        },
        [dispatch],
    );

    return { success, error, info, warning };
};

const ToastComponent = ({ toast, onClose }: { toast: ToastMessage; onClose: () => void }) => {
    const getVariant = (type: ToastMessage['type']) => {
        switch (type) {
            case 'success':
                return 'success';
            case 'error':
                return 'danger';
            case 'info':
                return 'primary';
            case 'warning':
                return 'warning';
            default:
                return 'light';
        }
    };

    const formatTime = (timestamp: number) => {
        return new Intl.DateTimeFormat(navigator.language, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(timestamp));
    };

    return (
        <Toast onClose={onClose} show={true} delay={3000} autohide bg={getVariant(toast.type)}>
            <Toast.Header>
                <strong className="me-auto">{toast.title}</strong>
                <small>{formatTime(toast.id)}</small>
            </Toast.Header>
            <Toast.Body>{toast.message}</Toast.Body>
        </Toast>
    );
};

export { ToastProvider, useToast };
